import {
	Paragraph,
	Ruler,
	Show,
	Skeleton,
	tokens,
} from '@welbe/web-design-system';
import { useContext, useMemo, useState } from 'react';
import { FiInfo } from 'react-icons/fi';
import {
	HealthRiskAssessmentContext,
	HealthRiskAssessmentContextType,
} from 'Views/HealthRiskAssessment/HealthRiskAssessment.context';
import { ActivitiesTypes } from 'Views/HealthRiskAssessment/HealthRiskAssessment.types';
import {
	ActivityContainer,
	ButtonsContainer,
	Container,
	EmptyActivityContainer,
	LoadingButtonsContainer,
	LoadingContainer,
	Wrapper,
} from './ActivityProgram.styles';
import { LeftSidePanel } from './components/LeftSidePanel/LeftSidePanel';
import { RightSidePanel } from './components/RightSidePanel/RightSidePanel';
import { ToggleButton } from './components/ToggleButton/ToggleButton';

export const ActivityProgram = (): JSX.Element => {
	const { assignedGroups, selectedGroup, setSelectedGroup, isLoading } =
		useContext<HealthRiskAssessmentContextType>(HealthRiskAssessmentContext);
	const [selectedActivity, setSelectedActivity] = useState<ActivitiesTypes>();
	const [focusedActivity, setFocusedActivity] = useState<string>('');

	const handleSelectActivity = (activityId: string) => {
		const activity = selectedGroup?.activities?.find(
			(activity: ActivitiesTypes) => activity.id === activityId,
		);

		setFocusedActivity(activityId);
		setSelectedActivity(activity);
	};

	const assignedActivities = useMemo(() => {
		const filteredActivities =
			selectedGroup?.activities.filter(
				(activity) => activity.activityAssigned === true,
			) || [];

		setFocusedActivity(filteredActivities[0]?.id || '');
		setSelectedActivity(filteredActivities[0]);

		return filteredActivities;
	}, [selectedGroup]);

	if (isLoading) {
		return (
			<LoadingContainer>
				<Skeleton width="50%" height="32px" />
				<LoadingButtonsContainer>
					<Skeleton width="125px" height="32px" />
					<Skeleton width="125px" height="32px" />
					<Skeleton width="125px" height="32px" />
					<Skeleton width="125px" height="32px" />
				</LoadingButtonsContainer>
				<Skeleton width="100%" height="300px" />
			</LoadingContainer>
		);
	}

	if (!assignedGroups?.length) {
		return (
			<Container>
				<FiInfo color={tokens.color_gray_dark} />
				<Paragraph fontWeight="500">No se encontraron resultados</Paragraph>
			</Container>
		);
	}

	return (
		<Wrapper>
			<Paragraph fontWeight="600" fontSize="16px" color={tokens.color_primary}>
				Actividades programadas por grupo de riesgo
			</Paragraph>

			<ButtonsContainer>
				{assignedGroups.map((group) => (
					<ToggleButton
						key={group.id}
						isActive={selectedGroup?.id === group.id}
						onToggle={() => setSelectedGroup(group)}
					>
						{group.name}
					</ToggleButton>
				))}
			</ButtonsContainer>

			<Show
				when={Boolean(selectedGroup?.isAssigned)}
				fallback={
					<EmptyActivityContainer>
						<FiInfo color={tokens.color_gray_dark} />
						<Paragraph fontStyle="italic">
							Seleccione un grupo de riesgo
						</Paragraph>
					</EmptyActivityContainer>
				}
			>
				<ActivityContainer>
					<LeftSidePanel
						focusedActivity={focusedActivity}
						activities={assignedActivities}
						onSelectActivity={(activityId: string) =>
							handleSelectActivity(activityId)
						}
					/>
					<Ruler orientation="vertical" />
					<RightSidePanel
						empty={!focusedActivity}
						activity={selectedActivity}
					/>
				</ActivityContainer>
			</Show>
		</Wrapper>
	);
};
